// Which route-history action a keydown asks for, or null.
//
// The undo button has always been there, bottom-left of the map, but a user reported "an undo
// function would be great": on desktop the hand goes to Ctrl+Z, finds nothing, and concludes the
// feature is missing. Kept as a pure function so the key table is testable without mounting a map.
const EDITABLE_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

const _isTypingTarget = (target) => {
    if (!target || !target.tagName) {
        return false;
    }

    return EDITABLE_TAGS.includes(target.tagName) || target.isContentEditable === true;
};

// `canUndo` is the caller's answer to "is there a step to take back?". It matters because undo()
// falls back to clearing the whole route when the snapshot stack is empty — which is the state of a
// route restored at load. A deliberate click on the button is one thing; Ctrl+Z is a reflex, and it
// would wipe the route and its local copy.
// `blocked` covers the case where something is layered over the map (a dialog): the map must not
// change behind it.
export const undoActionForKeydown = (event, {canUndo = true, blocked = false} = {}) => {
    // A held key would unwind the whole history at the repeat rate.
    if (!event || event.repeat || blocked || _isTypingTarget(event.target)) {
        return null;
    }

    // Ctrl on Windows/Linux, Cmd on macOS — never both, and never with Alt (that's another shortcut).
    const commandKey = event.ctrlKey !== event.metaKey;
    if (!commandKey || event.altKey) {
        return null;
    }

    const key = String(event.key || '').toLowerCase();

    if (key === 'z') {
        if (event.shiftKey) {
            return 'redo';
        }

        return canUndo ? 'undo' : null;
    }

    // Ctrl+Y is the other redo people reach for on Windows.
    if (key === 'y' && !event.shiftKey) {
        return 'redo';
    }

    return null;
};
